import React, { useState } from 'react';
import { Box, Checkbox, Link, Typography } from '@mui/material';
import { BlocksModalWrapper } from 'components/BlocksList/BlocksModals/BlocksModalWrapper';
import { SubmitBlocksTypes } from 'components/BlocksList/constants';
import { getBlocksProjectCount } from 'components/BlocksList/utilities';
import { useConfirmDeleteModalStyles } from 'components/TeamOverview/components/ConfirmActionModal/styles';
import { useContributeBlocksMutation } from 'store/apis/teamsApi';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';

export const ContributeBlocksModal = ({
  open,
  handleClose,
  selectedRows,
  resetSelectedRows,
}) => {
  const { classes } = useConfirmDeleteModalStyles();
  const { showSuccess, showError } = useSnackbar();
  const [contributeBlocks] = useContributeBlocksMutation();
  const [isFirstAgreementChecked, setIsFirstAgreementChecked] = useState(false);
  const [isSecondAgreementChecked, setIsSecondAgreementChecked] =
    useState(false);
  const [isThirdAgreementChecked, setIsThirdAgreementChecked] = useState(false);
  const projectsCount = getBlocksProjectCount(selectedRows);
  const text = `YOU'RE CONTRIBUTING ${selectedRows.length} BLOCKS FROM ${projectsCount} PROJECTS`;

  const handleSubmit = () => {
    const blockUUIDs = selectedRows.map((row) => row.uuid);

    contributeBlocks({ block_uuids: blockUUIDs })
      .unwrap()
      .then(() => {
        handleClose();
        resetSelectedRows();
        showSuccess('Blocks contribution request has been sent');
      })
      .catch(() => {
        handleClose();
        showError('Failed to contribute blocks');
      });
  };

  return (
    <BlocksModalWrapper
      open={open}
      handleClose={handleClose}
      actionType={SubmitBlocksTypes.CONTRIBUTE}
      disabled={
        !isFirstAgreementChecked ||
        !isSecondAgreementChecked ||
        !isThirdAgreementChecked
      }
      text={text}
      handleSubmit={handleSubmit}
    >
      <Box>
        <Typography className={classes.warning}>
          Warning: Contribution of Blocks
        </Typography>
        <Typography className={classes.agreementText}>
          You are about to contribute the selected blocks.{' '}
          <b>
            Contribution means permanently donating your data to HistoWiz,
            allowing it to be used for broader scientific discovery.
          </b>
        </Typography>
        <Typography mt={2} className={classes.agreementText}>
          Before proceeding, ensure your experiment has concluded and that you
          no longer need access to this data.
        </Typography>
        <Typography mt={2} className={classes.agreementText}>
          Once confirmed, these blocks will be placed in a queue for
          contribution. You will{' '}
          <b>no longer be able to view them on PathologyMap</b>, and any
          associated hyperlinks will become plain text.
        </Typography>
        <Box marginTop={2} paddingLeft={1}>
          <Box>
            <Checkbox
              size="medium"
              checked={isFirstAgreementChecked}
              onChange={() => setIsFirstAgreementChecked((prev) => !prev)}
            />
            <Typography component="span" className={classes.agreementText}>
              I confirm that my experiment has concluded, that I have no
              regulatory storage requirements, and that these blocks will no
              longer be needed.
            </Typography>
          </Box>
          <Box>
            <Checkbox
              size="medium"
              checked={isSecondAgreementChecked}
              onChange={() => setIsSecondAgreementChecked((prev) => !prev)}
            />
            <Typography component="span" className={classes.agreementText}>
              I have read the{' '}
              <Link
                href="https://home.histowiz.com/terms/#blocks"
                target="_blank"
              >
                terms and conditions
              </Link>{' '}
              and understand that HistoWiz <b>cannot guarantee access</b> to
              these blocks after confirmation.
            </Typography>
          </Box>
          <Box>
            <Checkbox
              size="medium"
              checked={isThirdAgreementChecked}
              onChange={() => setIsThirdAgreementChecked((prev) => !prev)}
            />
            <Typography component="span" className={classes.agreementText}>
              <b>I understand this action is permanent and cannot be undone.</b>
            </Typography>
          </Box>
        </Box>
      </Box>
    </BlocksModalWrapper>
  );
};
